import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/money";
import { Plus, ArrowRight, Warning, WarningCircle, TrendUp } from "@phosphor-icons/react";
import NewProposalModal from "@/components/NewProposalModal";
import NewPacketModal from "@/components/NewPacketModal";
import { weekLabel } from "@/components/CompWeeksTable";
import { StatCard, REVIEWABLE } from "./shared";

const hrs = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

// Past 8 weeks, colour-coded: red rows missed break-even (hours + dollars short), green rows cleared it.
function RecentWeeks({ weeks, be }) {
  const shown = weeks.filter((w) => !w.before_plan);
  if (shown.length === 0) return null;
  const missed = shown.filter((w) => w.applies !== "commission").length;
  const maxHrs = Math.max(be, ...shown.map((w) => w.hours)) || 1;
  return (
    <div className="mb-6" data-testid="sales-recent-weeks">
      <div className="flex items-end justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-lg font-black">Last {shown.length} weeks vs. break-even</h2>
        <div className="text-sm font-bold" data-testid="sales-recent-summary">
          <span className={missed ? "text-rose-700" : "text-emerald-700"}>{missed} week{missed === 1 ? "" : "s"} below</span>
          <span className="text-slate-400"> · </span>
          <span className="text-emerald-700">{shown.length - missed} above</span>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-2.5">Week</th>
              <th className="text-left px-4 py-2.5 w-[32%]">Approved hrs vs. {hrs(be)} break-even</th>
              <th className="text-right px-4 py-2.5">Hours</th>
              <th className="text-right px-4 py-2.5">Dollars</th>
              <th className="text-right px-4 py-2.5">Paid</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((w) => {
              const over = w.applies === "commission";
              const gapH = over ? w.hours_over_break_even : w.hours_to_break_even;
              const gapD = over ? w.commission - w.weekly_draw : w.dollars_short;
              return (
                <tr key={w.week_start} className={`border-t border-l-4 ${over ? "border-l-emerald-500 bg-emerald-50/40" : "border-l-rose-500 bg-rose-50/50"}`} data-testid={`recent-week-${w.week_start}`}>
                  <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{weekLabel(w.week_start)}</td>
                  <td className="px-4 py-2.5">
                    <div className="relative h-3 rounded bg-slate-200 overflow-hidden">
                      <div className={`h-full ${over ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${Math.min(100, (w.hours / maxHrs) * 100)}%` }} />
                      <div className="absolute top-0 bottom-0 w-0.5 bg-slate-900" style={{ left: `${Math.min(100, (be / maxHrs) * 100)}%` }} title={`${hrs(be)} hrs break-even`} />
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{hrs(w.hours)} hrs</div>
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono font-black whitespace-nowrap ${over ? "text-emerald-700" : "text-rose-700"}`} data-testid={`recent-gap-hours-${w.week_start}`}>{over ? "+" : "−"}{hrs(gapH)} hrs</td>
                  <td className={`px-4 py-2.5 text-right font-mono font-black whitespace-nowrap ${over ? "text-emerald-700" : "text-rose-700"}`} data-testid={`recent-gap-dollars-${w.week_start}`}>{over ? "+" : "−"}{money(gapD)}</td>
                  <td className="px-4 py-2.5 text-right font-mono whitespace-nowrap">{money(w.pay)}<div className="text-[10px] uppercase tracking-wider text-slate-400 font-sans">{over ? "commission" : "draw"}</div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SalesHome() {
  const { autoSend } = useOutletContext();
  const [d, setD] = useState(null);
  const [drafts, setDrafts] = useState({ proposals: [], packets: [] });
  const [openProposal, setOpenProposal] = useState(false);
  const [openPacket, setOpenPacket] = useState(false);

  const load = () => {
    api.get("/sales/me/dashboard?weeks=9").then((r) => setD(r.data)).catch(() => {});
    Promise.all([api.get("/sales/proposals"), api.get("/sales/packets")])
      .then(([p, k]) => setDrafts({ proposals: Array.isArray(p.data) ? p.data : [], packets: Array.isArray(k.data) ? k.data : [] })).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;

  const cw = d.current_week, rep = d.rep;
  const past = d.weeks.slice(1, 9);
  const be = rep.break_even_hours || 0;
  const pct = be > 0 ? Math.min(100, Math.round((cw.hours / be) * 100)) : 0;
  const over = cw.applies === "commission";
  const needsMe = [...drafts.proposals.filter((x) => x.status === "changes_requested").map((x) => ({ ...x, kind: "proposal" })),
                   ...drafts.packets.filter((x) => x.status === "changes_requested").map((x) => ({ ...x, kind: "packet" }))];
  const waiting = drafts.proposals.filter((x) => x.status === "pending_review").length + drafts.packets.filter((x) => x.status === "pending_review").length;

  return (
    <div data-testid="sales-home">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="tinylabel text-emerald-700">Week of {weekLabel(cw.week_start)}</div>
          <h1 className="text-3xl font-black">Good to see you, {rep.name.split(" ")[0]}</h1>
          <p className="text-slate-500 mt-1 text-sm">Your projects, the hours they're producing, and where you stand against your weekly draw.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpenProposal(true)} data-testid="sales-new-proposal-btn"
                  className="h-11 px-4 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-md flex items-center gap-2"><Plus size={18} weight="bold" /> New proposal</button>
          <button onClick={() => setOpenPacket(true)} data-testid="sales-new-packet-btn"
                  className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md flex items-center gap-2"><Plus size={18} weight="bold" /> Send assignment packet</button>
        </div>
      </div>

      {needsMe.length > 0 && (
        <div className="mb-6 rounded-md border-2 border-orange-400 bg-orange-50 p-4" data-testid="sales-needs-attention">
          <div className="flex items-center gap-2 font-black text-orange-950"><Warning size={20} weight="fill" /> {needsMe.length} draft{needsMe.length > 1 ? "s" : ""} sent back with notes</div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {needsMe.map((x) => (
              <li key={x.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <Link to={`/sales/${x.kind === "proposal" ? "proposals" : "packets"}/${x.id}`} data-testid={`attention-${x.id}`} className="font-semibold text-orange-900 underline">
                  {x.kind === "proposal" ? `Proposal · ${x.company_name}` : `Packet · ${x.worker_name || x.worker_email} → ${x.project_name || x.company_name}`}
                </Link>
                <span className="text-orange-800 italic">“{x.review_note}”</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className={`lg:col-span-2 rounded-md border-2 p-5 ${over ? "bg-emerald-50 border-emerald-500" : "bg-rose-50 border-rose-500"}`} data-testid="sales-week-card">
          <div className={`-mx-5 -mt-5 mb-4 px-5 py-2.5 rounded-t flex items-center gap-2 text-white font-black uppercase tracking-widest text-sm ${over ? "bg-emerald-600" : "bg-rose-600"}`} data-testid="sales-week-verdict">
            {over ? <TrendUp size={18} weight="bold" /> : <WarningCircle size={18} weight="fill" />}
            {over ? "Above break-even — commission applies" : "Below break-even — draw applies"}
          </div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="tinylabel">This week · approved hours on your projects</div>
              <div className="text-5xl font-black tabular-nums mt-1" data-testid="sales-week-hours">{hrs(cw.hours)}<span className="text-xl text-slate-400 font-bold"> / {hrs(be)} hrs</span></div>
              {cw.pending_hours > 0 && <div className="text-xs text-amber-700 mt-1" data-testid="sales-week-pending">+{hrs(cw.pending_hours)} hrs still waiting on supervisor approval</div>}
            </div>
            <div className={`text-right rounded-md px-4 py-2 border ${over ? "bg-white border-emerald-300" : "bg-white border-rose-300"}`} data-testid="sales-week-gap">
              <div className={`tinylabel ${over ? "text-emerald-700" : "text-rose-700"}`}>{over ? "Over break-even by" : "Short of break-even by"}</div>
              <div className={`text-3xl font-black tabular-nums leading-tight ${over ? "text-emerald-700" : "text-rose-700"}`} data-testid="sales-week-gap-hours">{over ? "+" : "−"}{hrs(over ? cw.hours_over_break_even : cw.hours_to_break_even)} hrs</div>
              <div className={`text-xl font-black tabular-nums ${over ? "text-emerald-700" : "text-rose-700"}`} data-testid="sales-week-gap-dollars">{over ? "+" : "−"}{money(over ? cw.commission - cw.weekly_draw : cw.dollars_short)}</div>
            </div>
            <div className="text-right">
              <div className="tinylabel">Pay this week</div>
              <div className="text-3xl font-black tabular-nums" data-testid="sales-week-pay">{money(cw.pay)}</div>
              <div className="text-xs font-bold mt-0.5 text-slate-600" data-testid="sales-week-applies">
                {over ? `${hrs(cw.hours)} hrs × ${money(rep.rate_per_hour)} commission` : `${money(rep.weekly_draw)} draw (commission would be ${money(cw.commission)})`}
              </div>
            </div>
          </div>
          {be > 0 && (
            <div className="mt-4">
              <div className="h-4 rounded-full bg-white border border-slate-200 overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${over ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${pct}%` }} data-testid="sales-break-even-bar" />
              </div>
              <div className="flex justify-between text-xs text-slate-600 mt-1">
                <span>{money(rep.weekly_draw)} draw ÷ {money(rep.rate_per_hour)}/hr = <b>{hrs(be)} hrs</b> to break even</span>
                <span className="font-bold">{pct}% of break-even</span>
              </div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Active projects" value={d.active_project_count} testId="sales-stat-projects" />
          <StatCard label="Workers on your jobs" value={d.active_worker_count} testId="sales-stat-workers" />
          <StatCard label="YTD pay" value={money(d.ytd.pay)} sub={`${d.ytd.weeks_over_break_even} wks over break-even`} tone="emerald" testId="sales-stat-ytd" />
          <StatCard label={autoSend ? "Awaiting approval (older)" : "Awaiting admin approval"} value={waiting} sub={<><Link to="/sales/proposals" className="underline">proposals</Link> · <Link to="/sales/packets" className="underline">packets</Link></>} tone={waiting ? "amber" : "slate"} testId="sales-stat-waiting" />
        </div>
      </div>

      <RecentWeeks weeks={past} be={be} />

      <div className="flex items-end justify-between mb-2">
        <h2 className="text-lg font-black">Your projects</h2>
        <Link to="/sales/comp" className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1" data-testid="sales-see-comp">Week-by-week comp <ArrowRight size={14} weight="bold" /></Link>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Client · Project</th>
              <th className="text-left px-4 py-3">Location</th>
              <th className="text-right px-4 py-3">Bill rate</th>
              <th className="text-left px-4 py-3">Workers (hrs this week)</th>
              <th className="text-right px-4 py-3">Hrs this week</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {d.projects.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No projects credited to you yet. Once a proposal you submitted is accepted, the project shows up here.</td></tr>
            )}
            {d.projects.map((p) => (
              <tr key={p.id} className={`border-t border-slate-100 ${p.status !== "active" ? "opacity-60" : ""}`} data-testid={`sales-project-${p.id}`}>
                <td className="px-4 py-3"><div className="text-xs text-slate-500">{p.client_name}</div><div className="font-semibold">{p.name}</div>{p.po_number && <div className="text-[11px] text-slate-400">PO {p.po_number}</div>}</td>
                <td className="px-4 py-3 text-slate-600">{p.location || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{p.default_bill_rate ? `${money(p.default_bill_rate)}/hr` : "—"}</td>
                <td className="px-4 py-3">
                  {p.workers.filter((w) => w.status === "active").length === 0 && <span className="text-slate-400 text-xs">No one assigned yet</span>}
                  {p.workers.filter((w) => w.status === "active").map((w) => (
                    <div key={w.id} className="text-slate-700">{w.name} <span className="text-xs text-slate-400 font-mono">{hrs(w.hours_this_week)}h</span></div>
                  ))}
                </td>
                <td className="px-4 py-3 text-right font-mono font-bold">{hrs(p.hours_this_week)}</td>
                <td className="px-4 py-3"><span className={`pill pill-${p.status}`}>{(p.status || "").toUpperCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openProposal && <NewProposalModal mode="sales" autoSend={autoSend} onClose={() => setOpenProposal(false)} onCreated={load} />}
      {openPacket && <NewPacketModal mode="sales" autoSend={autoSend} onClose={() => setOpenPacket(false)} onSent={load} />}
    </div>
  );
}
